import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

export interface RouteInfo {
  isSearchBySize: boolean;
  hasRearSpecs: boolean;
  isStaggeredSetup: boolean;
  routePath: string;
}

@Injectable({
  providedIn: 'root'
})
export class WheelRouteDetectionService {

  /**
   * Detects the current search type and wheel configuration
   */
  detectRouteInfo(route: ActivatedRoute, queryParams?: any): RouteInfo {
    const routePath = route.snapshot.url[0]?.path || '';
    const isSearchBySize = routePath === 'search-by-size';
    
    let hasRearSpecs = false;
    let isStaggeredSetup = false;
    
    if (queryParams) {
      // Check if rear specifications are provided
      hasRearSpecs = !!(queryParams.rear_rim_diameter || queryParams.rear_rim_width);
      
      // Check if it's a true staggered setup (different front/rear specs)
      isStaggeredSetup = hasRearSpecs && (
        queryParams.front_rim_diameter !== queryParams.rear_rim_diameter ||
        queryParams.front_rim_width !== queryParams.rear_rim_width ||
        queryParams.offset !== queryParams.rear_offset
      );
    }
    
    return {
      isSearchBySize,
      hasRearSpecs,
      isStaggeredSetup,
      routePath
    };
  }

  /**
   * Determines if the current route is search-by-size
   */
  isSearchBySize(route: ActivatedRoute): boolean {
    return route.snapshot.url[0]?.path === 'search-by-size';
  }

  /**
   * Determines if the current route is regular vehicle search
   */
  isRegularSearch(route: ActivatedRoute): boolean {
    return route.snapshot.url[0]?.path === 'serchvehicle';
  }

  /**
   * Gets the search type as a string
   */
  getSearchType(route: ActivatedRoute): 'search-by-size' | 'regular' | 'unknown' {
    const path = route.snapshot.url[0]?.path;
    
    if (path === 'search-by-size') {
      return 'search-by-size';
    } else if (path === 'serchvehicle') {
      return 'regular';
    } else {
      return 'unknown';
    }
  }

  /**
   * Checks if rear wheel specifications should be shown in UI
   */
  shouldShowRearOptions(queryParams: any): boolean {
    return !!(queryParams?.rear_rim_diameter || queryParams?.rear_rim_width);
  }

  /**
   * Determines the appropriate component to use for size selection
   */
  getSizeSelectionComponent(routeInfo: RouteInfo): 'regular' | 'search-by-size' | 'staggered' {
    if (!routeInfo.isSearchBySize) {
      return 'regular';
    }
    
    if (routeInfo.isStaggeredSetup) {
      return 'staggered';
    }
    
    return 'search-by-size';
  }
}
